import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DocumentArrowDownIcon, TableCellsIcon, DocumentTextIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';

const API = import.meta.env.VITE_API_URL || '';

const REPORTS = [
  {
    key: 'distance',
    label: 'Distância Percorrida',
    description: 'Km total, velocidade máxima e média por veículo no período',
    icon: '📍',
  },
  {
    key: 'usage',
    label: 'Uso vs Ociosidade',
    description: 'Percentual de tempo em movimento e parado por veículo',
    icon: '⏱️',
  },
  {
    key: 'speed-violations',
    label: 'Infrações de Velocidade',
    description: 'Registros de excesso de velocidade com detalhes e localização',
    icon: '🚨',
  },
  {
    key: 'alerts',
    label: 'Alertas GPS',
    description: 'Todos os alertas gerados: zonas, horários, offline, velocidade',
    icon: '🔔',
  },
];

const ALERT_TYPES = [
  { value: '', label: 'Todos os tipos' },
  { value: 'speed', label: 'Velocidade' },
  { value: 'offline', label: 'Offline' },
  { value: 'idle_outside_zone', label: 'Local indesejado' },
  { value: 'outside_hours', label: 'Fora de horário' },
  { value: 'geofence_entry', label: 'Entrada de zona' },
  { value: 'geofence_exit', label: 'Saída de zona' },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function GpsReportsPage() {
  const [activeReport, setActiveReport] = useState('distance');
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ vehicleId: '', alertType: '', startDate: daysAgo(30), endDate: today() });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/reports/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setVehicles(r.data.data || []))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    if (!filters.startDate || !filters.endDate) { setError('Selecione o período'); return; }
    setLoading(true); setError(''); setData([]); setSearched(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ startDate: filters.startDate, endDate: filters.endDate });
      if (filters.vehicleId) params.set('vehicleId', filters.vehicleId);
      if (filters.alertType && activeReport === 'alerts') params.set('alertType', filters.alertType);
      const r = await axios.get(`${API}/api/reports/${activeReport}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar relatório');
    } finally { setLoading(false); }
  }, [activeReport, filters]);

  const exportFile = async (format) => {
    if (!filters.startDate || !filters.endDate) { setError('Selecione o período'); return; }
    setExporting(format);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ startDate: filters.startDate, endDate: filters.endDate });
      if (filters.vehicleId) params.set('vehicleId', filters.vehicleId);
      if (filters.alertType && activeReport === 'alerts') params.set('alertType', filters.alertType);
      const url = `${API}/api/reports/${activeReport}/export/${format}?${params}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Falha na exportação');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ext = format === 'excel' ? 'xlsx' : format;
      a.download = `viploc-${activeReport}-${filters.startDate}-${filters.endDate}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError('Erro ao exportar: ' + err.message);
    } finally { setExporting(''); }
  };

  const currentReport = REPORTS.find(r => r.key === activeReport);
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  const colLabel = {
    license_plate: 'Placa', vehicle: 'Veículo', km_total: 'Km Total',
    max_speed: 'Vel. Máx', avg_speed: 'Vel. Média', utilization_pct: 'Utilização',
    first_point: 'Início', last_point: 'Fim',
    points_moving: 'Em Movimento', points_idle: 'Parado', points_total: 'Total Pontos',
    pct_moving: '% Movimento', pct_idle: '% Ocioso',
    date: 'Data/Hora', driver_name: 'Motorista', speed_kmh: 'Velocidade',
    speed_limit: 'Limite', excesso: 'Excesso', resolved: 'Status', coordinates: 'Coordenadas',
    type: 'Tipo', severity: 'Severidade', title: 'Título', description: 'Detalhe',
  };

  const severityBadge = (v) => {
    if (v === 'Crítico') return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
    if (v === 'Atenção') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-5">

        {/* Título */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Relatórios GPS</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Análise de frota com exportação em Excel, CSV e PDF</p>
        </div>

        {/* Seletor de tipo de relatório */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {REPORTS.map(r => (
            <button key={r.key} onClick={() => { setActiveReport(r.key); setData([]); setSearched(false); setError(''); }}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                activeReport === r.key
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300'
              }`}
            >
              <div className="text-2xl mb-1">{r.icon}</div>
              <div className={`font-semibold text-sm ${activeReport === r.key ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}>{r.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-tight">{r.description}</div>
            </button>
          ))}
        </div>

        {/* Filtros */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Início</label>
              <input type="date" value={filters.startDate}
                onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Fim</label>
              <input type="date" value={filters.endDate}
                onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Veículo</label>
              <select value={filters.vehicleId} onChange={e => setFilters(f => ({ ...f, vehicleId: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="">Todos os veículos</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.license_plate} — {v.brand} {v.model}</option>
                ))}
              </select>
            </div>
            {activeReport === 'alerts' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tipo de Alerta</label>
                <select value={filters.alertType} onChange={e => setFilters(f => ({ ...f, alertType: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {ALERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Atalhos de período */}
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              { label: 'Hoje', s: today(), e: today() },
              { label: '7 dias', s: daysAgo(7), e: today() },
              { label: '30 dias', s: daysAgo(30), e: today() },
              { label: '90 dias', s: daysAgo(90), e: today() },
            ].map(p => (
              <button key={p.label} onClick={() => setFilters(f => ({ ...f, startDate: p.s, endDate: p.e }))}
                className="text-xs px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Barra de ações */}
        <div className="flex flex-wrap gap-3 items-center">
          <button onClick={fetchReport} disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors shadow-sm"
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
            {loading ? 'Carregando...' : 'Gerar Relatório'}
          </button>

          {searched && data.length > 0 && (
            <>
              <button onClick={() => exportFile('excel')} disabled={!!exporting}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors shadow-sm"
              >
                <DocumentArrowDownIcon className="w-4 h-4" />
                {exporting === 'excel' ? 'Gerando...' : 'Excel (.xlsx)'}
              </button>
              <button onClick={() => exportFile('csv')} disabled={!!exporting}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors shadow-sm"
              >
                <TableCellsIcon className="w-4 h-4" />
                {exporting === 'csv' ? 'Gerando...' : 'CSV'}
              </button>
              <button onClick={() => exportFile('pdf')} disabled={!!exporting}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors shadow-sm"
              >
                <DocumentTextIcon className="w-4 h-4" />
                {exporting === 'pdf' ? 'Gerando...' : 'PDF'}
              </button>
              <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">{data.length} registro{data.length !== 1 ? 's' : ''}</span>
            </>
          )}
        </div>

        {/* Erro */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {/* Tabela de preview */}
        {loading && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Processando dados GPS...</span>
            </div>
          </div>
        )}

        {!loading && searched && data.length === 0 && !error && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="text-4xl mb-3">{currentReport?.icon}</div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">Nenhum dado encontrado para o período</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Tente ampliar o intervalo de datas ou remover filtros</p>
          </div>
        )}

        {!loading && data.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <span className="text-lg">{currentReport?.icon}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{currentReport?.label}</span>
              <span className="ml-auto text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">{data.length} registros</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-700 text-white">
                    {columns.map(col => (
                      <th key={col} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap text-xs">
                        {colLabel[col] || col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'bg-white dark:bg-gray-800'}>
                      {columns.map(col => (
                        <td key={col} className="px-3 py-2 text-gray-800 dark:text-gray-200 whitespace-nowrap border-b border-gray-100 dark:border-gray-700">
                          {col === 'severity' ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${severityBadge(row[col])}`}>{row[col]}</span>
                          ) : col === 'resolved' ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${row[col] === 'Resolvido' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'}`}>{row[col]}</span>
                          ) : col === 'license_plate' ? (
                            <span className="font-mono font-bold text-blue-700 dark:text-blue-300">{row[col]}</span>
                          ) : (
                            row[col] ?? '—'
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!searched && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="text-5xl mb-4">{currentReport?.icon}</div>
            <p className="text-gray-600 dark:text-gray-300 font-medium text-lg">{currentReport?.label}</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">{currentReport?.description}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">Configure os filtros e clique em "Gerar Relatório"</p>
          </div>
        )}
      </div>
    </div>
  );
}
